import React, { useState, useRef, useCallback } from 'react';
import { uploadPropertyImages } from '../services/propertyImageService';
import { IMAGE_TYPE_OPTIONS } from '../utils/propertyUtils';

const MAX_FILE_SIZE_MB = 10;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * PropertyPhotoUploader
 *
 * Props:
 *   agencyId      {number}
 *   propertyId    {number}
 *   onUploadComplete  {function}  called after all uploads finish (success or partial)
 *   onCancel      {function}
 */
const PropertyPhotoUploader = ({ agencyId, propertyId, onUploadComplete, onCancel }) => {
  const [fileItems, setFileItems] = useState([]); // { id, file, imageType, preview, status, errorMsg }
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  let nextId = useRef(0);

  // ─── File validation ────────────────────────────────────────────────────────
  const validateFile = (file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return `${file.name}: unsupported format (use JPG, PNG, GIF, WebP)`;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `${file.name}: exceeds ${MAX_FILE_SIZE_MB} MB limit`;
    }
    return null;
  };

  const addFiles = useCallback((rawFiles) => {
    const valid = [];
    const errors = [];

    Array.from(rawFiles).forEach((file) => {
      const err = validateFile(file);
      if (err) {
        errors.push(err);
      } else {
        valid.push({
          id: nextId.current++,
          file,
          imageType: 'gallery',
          preview: URL.createObjectURL(file),
          status: 'pending', // 'pending' | 'uploading' | 'done' | 'error'
          errorMsg: null,
        });
      }
    });

    if (errors.length) {
      alert(errors.join('\n'));
    }

    setFileItems((prev) => [...prev, ...valid]);
  }, []);

  // ─── Drag and drop ──────────────────────────────────────────────────────────
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  };

  // ─── File input ─────────────────────────────────────────────────────────────
  const handleFileInput = (e) => {
    if (e.target.files.length) addFiles(e.target.files);
    e.target.value = ''; // reset so same file can be re-added
  };

  const removeFile = (id) => {
    setFileItems((prev) => {
      const item = prev.find((f) => f.id === id);
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((f) => f.id !== id);
    });
  };

  const updateType = (id, imageType) => {
    setFileItems((prev) => prev.map((f) => (f.id === id ? { ...f, imageType } : f)));
  };

  // ─── Upload ─────────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!fileItems.length) return;

    setUploading(true);

    // Group pending files by imageType
    const groups = {};
    fileItems
      .filter((f) => f.status === 'pending')
      .forEach((f) => {
        if (!groups[f.imageType]) groups[f.imageType] = [];
        groups[f.imageType].push(f);
      });

    // Mark all pending as 'uploading'
    setFileItems((prev) =>
      prev.map((f) => (f.status === 'pending' ? { ...f, status: 'uploading' } : f))
    );

    let anySuccess = false;

    for (const [imageType, items] of Object.entries(groups)) {
      const ids = items.map((i) => i.id);
      const files = items.map((i) => i.file);

      try {
        await uploadPropertyImages(agencyId, propertyId, files, imageType);
        anySuccess = true;
        setFileItems((prev) =>
          prev.map((f) => (ids.includes(f.id) ? { ...f, status: 'done' } : f))
        );
      } catch (err) {
        setFileItems((prev) =>
          prev.map((f) =>
            ids.includes(f.id) ? { ...f, status: 'error', errorMsg: err.message } : f
          )
        );
      }
    }

    setUploading(false);

    if (anySuccess) {
      // Revoke all previews and notify parent
      fileItems.forEach((f) => URL.revokeObjectURL(f.preview));
      onUploadComplete();
    }
  };

  const pendingCount = fileItems.filter((f) => f.status === 'pending').length;
  const hasErrors = fileItems.some((f) => f.status === 'error');

  return (
    <div className="card border-0 bg-light mb-4">
      <div className="card-body p-4">

        {/* Drop zone */}
        <div
          className={`border-2 rounded-3 p-5 text-center mb-4 transition-all ${
            isDragOver
              ? 'border-primary bg-primary bg-opacity-10'
              : 'border-dashed border-secondary'
          }`}
          style={{ borderStyle: 'dashed', cursor: 'pointer' }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <i className="bi bi-cloud-upload fs-1 text-muted"></i>
          <p className="mb-1 fw-semibold text-secondary mt-2">
            Drag &amp; drop photos here, or <span className="text-primary">browse</span>
          </p>
          <p className="small text-muted mb-0">
            JPG, PNG, GIF, WebP — max {MAX_FILE_SIZE_MB} MB per file
          </p>
          <input
            ref={fileInputRef}
            type="file"
            className="d-none"
            multiple
            accept={ACCEPTED_TYPES.join(',')}
            onChange={handleFileInput}
          />
        </div>

        {/* File list */}
        {fileItems.length > 0 && (
          <div className="mb-3" style={{ maxHeight: '340px', overflowY: 'auto' }}>
            {fileItems.map((item) => (
              <div
                key={item.id}
                className="d-flex align-items-start gap-3 p-3 mb-2 bg-white rounded-3 border"
              >
                {/* Thumbnail */}
                <img
                  src={item.preview}
                  alt={item.file.name}
                  style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                />

                {/* Info */}
                <div className="flex-grow-1 min-w-0">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <span className="fw-semibold text-truncate small" title={item.file.name}>
                      {item.file.name}
                    </span>
                    <span className="text-muted small ms-2 flex-shrink-0">
                      {formatBytes(item.file.size)}
                    </span>
                  </div>

                  {/* Type selector */}
                  <select
                    className="form-select form-select-sm w-auto d-inline-block"
                    value={item.imageType}
                    disabled={item.status !== 'pending'}
                    onChange={(e) => updateType(item.id, e.target.value)}
                    style={{ minWidth: 130 }}
                  >
                    {IMAGE_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>

                  {/* Status */}
                  {item.status === 'uploading' && (
                    <div className="progress mt-2" style={{ height: 4 }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                        style={{ width: '100%' }}
                      ></div>
                    </div>
                  )}
                  {item.status === 'done' && (
                    <span className="small text-success ms-2">
                      <i className="bi bi-check-circle-fill me-1"></i>Uploaded
                    </span>
                  )}
                  {item.status === 'error' && (
                    <span className="small text-danger ms-2" title={item.errorMsg}>
                      <i className="bi bi-exclamation-triangle-fill me-1"></i>Failed
                    </span>
                  )}
                </div>

                {/* Remove */}
                {item.status === 'pending' && (
                  <button
                    type="button"
                    className="btn btn-link text-danger p-0 flex-shrink-0"
                    onClick={() => removeFile(item.id)}
                    title="Remove"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Footer actions */}
        <div className="d-flex align-items-center justify-content-between">
          <div>
            {hasErrors && (
              <span className="small text-danger me-3">
                <i className="bi bi-exclamation-triangle me-1"></i>
                Some files failed. Check your connection and try again.
              </span>
            )}
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={onCancel}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleUpload}
              disabled={uploading || pendingCount === 0}
            >
              {uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Uploading…
                </>
              ) : (
                <>
                  <i className="bi bi-cloud-upload me-2"></i>
                  Upload {pendingCount > 0 ? `${pendingCount} photo${pendingCount !== 1 ? 's' : ''}` : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyPhotoUploader;

